"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.changePasswordInput = exports.passwordResetSchema = exports.createUserInput = exports.ASSIGNABLE_ROLES = exports.auditEventSchema = exports.auditEventsQuerySchema = exports.AUDIT_CSV_MAX_ROWS = exports.AUDIT_PAGE_MAX = exports.accessChoicesSchema = exports.accessChoicesQuerySchema = exports.userAccessListSchema = exports.practiceReachSchema = exports.userAccessSchema = exports.userGroupGrantSchema = exports.updateUserAccessBodySchema = exports.userAccessParamsSchema = void 0;
// Wire shapes of Screen 10's API (SPEC §8 Screen 10, §9 `GET/PUT /api/admin/users/:userRef/access`
// and `GET /api/admin/audit`, §15.3; CR-001 §5.3 CRQ-026, §8; J-043), served under `/api/v1` as
// `/users/:userId/access` and `/audit-events` (D-973). No field carries PHI: access settings are
// flags, ACO codes and TINs, and an audit event carries ids and codes only (D-978).
const zod_1 = require("zod");
const schema_js_1 = require("../../db/schema.js");
const access_js_1 = require("../access.js");
/** A TIN as `ref.practice.tin` stores it: nine digits. */
const tinSchema = zod_1.z.string().regex(/^\d{9}$/);
const acoCodeSchema = zod_1.z.string().min(1).max(64);
const groupIdSchema = zod_1.z.number().int().positive().max(2_147_483_647);
// No `.meta({ id })` on a params or querystring schema: @fastify/swagger expands parameters inline.
exports.userAccessParamsSchema = zod_1.z.object({ userId: zod_1.z.uuid() });
/**
 * The whole of a user's module access. A PUT REPLACES it (SPEC §9 "set a user's PHI access and ACO
 * access"): every field is required, and an ACO or grant left out is removed. Unknown keys are
 * refused, so a body cannot look as if it set a role; roles stay in Better Auth's admin tooling.
 */
exports.updateUserAccessBodySchema = zod_1.z.strictObject({
    phiAccess: zod_1.z.boolean(),
    /**
     * J-383 (Louis, 2026-10-29): the account is offered the Gap Closure Workbook. Absent leaves it as stored (on
     * for a new account), so an older client never turns it off.
     */
    gapClosureWorkbook: zod_1.z.boolean().optional(),
    /** Every ACO, now and later; for a practice role its grants still decide (CRQ-022). */
    allAcos: zod_1.z.boolean(),
    acoCodes: zod_1.z.array(acoCodeSchema).max(200),
    /** CR-001 §6.6: (ACO, TIN) pairs; each TIN must be a configured practice of that ACO (J-080). */
    practiceGrants: zod_1.z.array(zod_1.z.strictObject({ acoCode: acoCodeSchema, tin: tinSchema })).max(1000),
    /**
     * J-278 (QB3 item 11b): a practice role's performance group grants for one year, by group id; they
     * replace that year's group grants and keep every other year's. Absent leaves every group grant as
     * it is, so an older client never wipes them. Each group must be a non-ACO-wide group of that year.
     */
    groupGrants: zod_1.z
        .strictObject({
        year: zod_1.z.number().int().min(2000).max(2100),
        groupIds: zod_1.z.array(groupIdSchema).max(500),
    })
        .optional(),
});
/**
 * J-278 (QB3 item 11b): one performance group a user is granted (`core.user_group_access`), with its
 * name, its number of practices and the ACOs they are in. Organization identifiers only.
 */
exports.userGroupGrantSchema = zod_1.z
    .object({
    groupId: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    practiceCount: zod_1.z.number().int(),
    acoCodes: zod_1.z.array(zod_1.z.string()),
})
    .meta({ id: "AcoUserGroupGrant" });
exports.userAccessSchema = zod_1.z
    .object({
    userId: zod_1.z.string(),
    /** The Better Auth role: a practice role is scoped by `practiceGrants`, any other by the ACOs. */
    role: zod_1.z.string(),
    phiAccess: zod_1.z.boolean(),
    /** J-383: the account is offered the Gap Closure Workbook (on unless an admin turned it off). */
    gapClosureWorkbook: zod_1.z.boolean(),
    allAcos: zod_1.z.boolean(),
    acoCodes: zod_1.z.array(zod_1.z.string()),
    practiceGrants: zod_1.z.array(access_js_1.practiceGrantSchema),
    /**
     * J-278: the performance groups granted, newest year first. A practice role sees each one's
     * practices; any other role holding one is group-scoped (J-238, set by `users:create-group-user`).
     */
    groupGrants: zod_1.z.array(exports.userGroupGrantSchema),
    /** A current TENANT role assignment, without which every ACO route answers 403. */
    tenantAssignment: zod_1.z.boolean(),
})
    .meta({ id: "AcoUserAccess" });
/**
 * J-278 (QB3 item 11c): the practices a user's practice grants and performance group grants reach, as
 * counts within the admin's ACOs: in total, the groups granted, the practices granted beyond those
 * groups', and per ACO. Zero everywhere for a user holding neither.
 */
exports.practiceReachSchema = zod_1.z.object({
    practices: zod_1.z.number().int(),
    groups: zod_1.z.number().int(),
    morePractices: zod_1.z.number().int(),
    byAco: zod_1.z.array(zod_1.z.object({ acoCode: zod_1.z.string(), practices: zod_1.z.number().int() })),
});
/** CR-005 item 10 (J-191): Users & Access's Access column, every user holding any access at once. */
exports.userAccessListSchema = zod_1.z
    .object({
    users: zod_1.z.array(zod_1.z.object({
        userId: zod_1.z.string(),
        phiAccess: zod_1.z.boolean(),
        allAcos: zod_1.z.boolean(),
        /** The ACO set and the ACOs of any practice grant, sorted. */
        acoCodes: zod_1.z.array(zod_1.z.string()),
        /** J-278 (QB3 item 11c): the practices the user's practice and group grants reach. */
        reach: exports.practiceReachSchema,
    })),
})
    .meta({ id: "AcoUserAccessList" });
/**
 * J-278 (QB3 item 11): what an admin chooses a practice role's access from, for one year: the year's
 * performance groups (never an ACO-wide one) with their practices, and every configured practice with
 * the groups of that year that hold it. Within the admin's ACOs. Organization identifiers only.
 */
exports.accessChoicesQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100).optional(),
});
const choicePracticeSchema = zod_1.z.object({
    acoCode: zod_1.z.string(),
    tin: zod_1.z.string(),
    practiceName: zod_1.z.string().nullable(),
});
exports.accessChoicesSchema = zod_1.z
    .object({
    /** The year shown: the one asked, else the newest year with performance groups; null with none. */
    performanceYear: zod_1.z.number().int().nullable(),
    /** Every year with performance groups, newest first. */
    years: zod_1.z.array(zod_1.z.number().int()),
    groups: zod_1.z.array(zod_1.z.object({
        groupId: zod_1.z.number().int(),
        groupName: zod_1.z.string(),
        /** The ACOs of its practices, sorted. */
        acoCodes: zod_1.z.array(zod_1.z.string()),
        practices: zod_1.z.array(choicePracticeSchema),
    })),
    practices: zod_1.z.array(choicePracticeSchema.extend({
        /** The year's performance groups holding this practice. */
        groupIds: zod_1.z.array(zod_1.z.number().int()),
    })),
})
    .meta({ id: "AcoAccessChoices" });
/** The largest page of `GET /audit-events`; the CSV form has its own cap, `AUDIT_CSV_MAX_ROWS`. */
exports.AUDIT_PAGE_MAX = 1000;
/** A CSV export holds every matching row up to this many; more is refused, never truncated. */
exports.AUDIT_CSV_MAX_ROWS = 10_000;
exports.auditEventsQuerySchema = zod_1.z
    .object({
    /** The acting user (`actor_user_id`). */
    userId: zod_1.z.uuid().optional(),
    /**
     * Events by this user or about them (J-073): their own actions, their access changes
     * (`aco_access` `user:<id>`) and their account changes (`user` `<id>`).
     */
    subjectUserId: zod_1.z.uuid().optional(),
    action: zod_1.z.enum(schema_js_1.auditAction.enumValues).optional(),
    /** The resource's type, e.g. `analysis_bundle` (J-185: the Data page's Audit link). */
    resourceType: zod_1.z.string().min(1).max(64).optional(),
    /** The resource's id as the row holds it, e.g. `bundle:<uuid>`; an opaque id, never PHI. */
    resourceId: zod_1.z.string().min(1).max(200).optional(),
    /** Inclusive start, an ISO 8601 date-time with an offset. */
    from: zod_1.z.iso.datetime({ offset: true }).optional(),
    /** Exclusive end. */
    to: zod_1.z.iso.datetime({ offset: true }).optional(),
    /** `csv` downloads every matching row, up to `AUDIT_CSV_MAX_ROWS`; `limit` then does not apply. */
    format: zod_1.z.enum(["json", "csv"]).default("json"),
    /** Newest first, at most this many rows (JSON only). */
    limit: zod_1.z.coerce.number().int().min(1).max(exports.AUDIT_PAGE_MAX).default(100),
    /**
     * The next page (J-210, inbox 06 PR E point 9): only events older than this `id`, the last id of
     * the page before. JSON only; the CSV form is one file.
     */
    beforeId: zod_1.z
        .string()
        .regex(/^\d{1,19}$/)
        .optional(),
})
    .refine((q) => q.from === undefined || q.to === undefined || Date.parse(q.from) < Date.parse(q.to), {
    message: "The end day is before the start day. Choose an end day on or after the start day.",
    path: ["to"],
});
exports.auditEventSchema = zod_1.z
    .object({
    /** `audit_events.id`, a bigint, as a string. */
    id: zod_1.z.string(),
    occurredAt: zod_1.z.string(),
    actorUserId: zod_1.z.string().nullable(),
    actorRole: zod_1.z.string().nullable(),
    action: zod_1.z.enum(schema_js_1.auditAction.enumValues),
    outcome: zod_1.z.enum(schema_js_1.auditOutcome.enumValues),
    resourceType: zod_1.z.string(),
    resourceId: zod_1.z.string().nullable(),
    /** Method and route pattern, never a raw URL. */
    route: zod_1.z.string().nullable(),
    requestId: zod_1.z.string().nullable(),
    /** Parameters only: field names, counts and reason codes (D-978). */
    details: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
})
    .meta({ id: "AcoAuditEvent" });
/** The roles Screen 10 assigns (SPEC §15.2; CR-001 practice roles); `clinician` stays the shell's. */
exports.ASSIGNABLE_ROLES = [
    "admin",
    "analyst",
    "viewer",
    "practice_manager",
    "practice_staff",
];
/**
 * Screen 10's new account (J-073), sent to Better Auth's `POST /admin/create-user`. The password
 * bounds are the server's (`minPasswordLength` 15, `maxPasswordLength` 128, `plugins/auth.ts`).
 */
exports.createUserInput = zod_1.z.object({
    email: zod_1.z.email("enter an email address"),
    name: zod_1.z.string().trim().min(1, "enter a name"),
    password: zod_1.z.string().min(15, "at least 15 characters").max(128, "at most 128 characters"),
    role: zod_1.z.enum(exports.ASSIGNABLE_ROLES),
});
/**
 * J-244 (item 4a): the answer to an admin's password reset, `POST /users/:userId/password-resets`. The
 * one-time password is generated on the server, returned in this answer only, and never stored in
 * readable form or logged (`oneTimePassword` is in the logger's redaction list). The account must change
 * it at its next sign-in.
 */
exports.passwordResetSchema = zod_1.z
    .object({
    userId: zod_1.z.string(),
    oneTimePassword: zod_1.z.string(),
})
    .meta({ id: "PasswordReset" });
/**
 * J-244: the change-password form (Settings, and the page a new or reset account is sent to). The
 * bounds are the server's (`minPasswordLength` 15, `maxPasswordLength` 128); the breached-password
 * check and "different from the current one" are the server's to answer.
 */
exports.changePasswordInput = zod_1.z
    .object({
    currentPassword: zod_1.z.string().min(1, "enter your current password"),
    newPassword: zod_1.z.string().min(15, "at least 15 characters").max(128, "at most 128 characters"),
    confirmPassword: zod_1.z.string(),
})
    .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "the two new passwords differ",
});
//# sourceMappingURL=admin.js.map